/**
 * Import API Module
 *
 * This module provides functions for interacting with the import-related endpoints
 * of the WealthBee Enterprise API, including uploading files, retrieving import records,
 * and fetching import row details.
 */

import { Import } from "@/types/Import";
import axios from "axios";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientAndFundHeader, buildClientHeader } from "./buildHeader";

/**
 * Uploads a file to be imported using a specified import template
 *
 * @param file - The file to be imported
 * @param importTemplateId - The ID of the import template to use
 * @param fundId - The ID of the fund to associate with the import
 * @returns Promise resolving to a boolean indicating success or failure
 */
export const importFile = async (
  file: File,
  importTemplateId: number,
  fundId?: number,
  reportingPeriodId?: number,
  user?: string
): Promise<boolean> => {
  try {
    const data = new FormData();
    data.append("file", file);

    let url = `${getServiceEndpoint()}/imports?importTemplateId=${importTemplateId}`;
    if (fundId) {
      url += `&fundId=${fundId}`;
    }
    if (reportingPeriodId) {
      url += `&reportingPeriodId=${reportingPeriodId}`;
    }
    if (user) {
      url += `&user=${user}`;
    }
    const response = await axios.post(url, data, buildClientHeader("file"));

    if (response.status !== 200) {
      console.error("Failed to save import template", response.statusText);
      return false;
    }
  } catch (error) {
    console.error("Error saving import template:", error);
    return false;
  }
  return true;
};

/**
 * Retrieves a list of all imports
 *
 * @returns Promise resolving to an array of Import objects
 * @throws Error if authentication fails or API request fails
 */
export const getImports = async (): Promise<Import[]> => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("No authentication token found");
    }

    // TODO - pass selected fund

    const response = await axios.get(
      `${getServiceEndpoint()}/imports`,
      buildClientAndFundHeader(1)
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching imports:", error);
    throw error;
  }
};

/**
 * Retrieves the detailed rows for a specific import
 *
 * @param importId - The ID of the import to retrieve rows for
 * @returns Promise resolving to an array of import row data
 * @throws Error if authentication fails or API request fails
 */
export const getImportRows = async (importId: string): Promise<any[]> => {
  console.log("getImportRows", importId);
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("No authentication token found");
    }

    // TODO - selected fund
    const response = await axios.get(
      `${getServiceEndpoint()}/imports/${importId}`,
      buildClientAndFundHeader(1)
    );

    if (response.status === 401) {
      throw new Error("Unauthorized - Invalid or expired token");
    }

    if (response.status >= 400) {
      throw new Error(`Failed to fetch import rows: ${response.statusText}`);
    }

    const data = response.data;
    console.log("Import rows API response:", data);

    if (data.rows) {
      return data.rows;
    } else if (Array.isArray(data)) {
      return data;
    } else {
      throw new Error("Unexpected API response format");
    }
  } catch (error) {
    console.error("Error fetching import rows:", error);
    throw error;
  }
};
